import React, { FC, useEffect, useState } from 'react';
import { Form, Drawer, Button, Space, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useUserSlice } from '../../slice';
import { TextSemibold14 } from 'styles/typography';
import {
  IAccessRightsNumbers,
  IFormPermissions,
  PermissionsEnum,
  RoleEnum,
} from '../../slice/types';
import PermissionsRadioButtons from '../PermissionsRadioButtons/PermissionsRadioButtons';
import {
  selectAddUserCompanyFormState,
  selectUserAvailableCompanies,
} from '../../slice/selectors';

interface IFinishValues extends IFormPermissions {
  companyId: number;
}

interface IProps {
  email: string;
  userId: number;
}

const UserCompanyPermissionsForm: FC<IProps> = ({ email, userId }) => {
  const { t } = useTranslation();

  const { actions } = useUserSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [
    checkedPermissions,
    setCheckedPermissions,
  ] = useState<IAccessRightsNumbers | null>(null);

  const {
    loading,
    companyId,
    companyName,
    type,
    isOpen,
    permissions,
  } = useSelector(selectAddUserCompanyFormState);

  const envId = useSelector(selectSelectedEnvId);
  const companies = useSelector(selectUserAvailableCompanies);

  // Set edit company data
  useEffect(() => {
    if (type === 'edit' && form && isOpen && permissions && companyId) {
      form.setFieldsValue({
        ...permissions,
        companyId,
      });
      setCheckedPermissions(permissions);
    }
  }, [type, isOpen, form, permissions, companyId]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setCheckedPermissions(null);
    }
  }, [isOpen, form]);

  const onFinish = (values: IFinishValues) => {
    if (!envId) {
      return;
    }
    const { companyId: formCompanyId, ...formPermissions } = values;

    dispatch(
      actions.submitAddUserToCompany({
        email,
        userId,
        companyId: type === 'edit' && companyId ? companyId : formCompanyId,
        envId,
        role: RoleEnum.shared,
        permissions: formPermissions,
        type,
      }),
    );
  };

  const onClose = () => {
    dispatch(actions.toggleAddUserCompanyForm({ isOpen: false }));
  };

  const initialRadioButtonsValuesObj = Object.keys(PermissionsEnum).reduce(
    (accumulator: { [key: string]: number }, permission) => {
      accumulator[permission] = 0;
      return accumulator;
    },
    {},
  );

  const onChange = (
    changedItem: { [key: string]: number | string },
    values: IFinishValues,
  ) => {
    const { companyId, ...checkboxValues } = values;
    setCheckedPermissions(checkboxValues);
  };

  return (
    <Drawer
      title={
        type === 'add'
          ? t(
              'User.CompanyPermissionsForm.AddCompanyFormTitle',
              'Add user to company',
            )
          : t(
              'User.CompanyPermissionsForm.EditTitile',
              'Edit user permissions in company',
            )
      }
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={430}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>
              {t('User.CompanyPermissionsForm.Cancel', 'Cancel')}
            </Button>

            <Button
              loading={loading}
              form="userCompanyShareForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {type === 'add'
                ? t('User.CompanyPermissionsForm.Add', 'Add')
                : t('User.CompanyPermissionsForm.Update', 'Update')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical', ...initialRadioButtonsValuesObj }}
        onValuesChange={onChange}
        requiredMark="optional"
        id="userCompanyShareForm"
        onFinish={onFinish}
      >
        {type === 'edit' ? (
          <TextSemibold14>
            {t('User.CompanyPermissionsForm.CompanyName', {
              defaultValue: 'Company: {{companyName}}',
              companyName,
            })}
          </TextSemibold14>
        ) : (
          <Form.Item name={'companyId'} rules={[{ required: true }]}>
            <Select
              placeholder={t('Resource.TradeForm.Company', 'Company')}
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >=
                0
              }
              getPopupContainer={trigger => trigger.parentNode}
            >
              {companies.map(company => (
                <Select.Option key={company.id} value={company.id}>
                  {company.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <TextSemibold14 style={{ margin: '20px 0 10px', display: 'block' }}>
          {t('User.CompanyPermissionsForm.Permissions', 'Permissions')}
        </TextSemibold14>

        <PermissionsRadioButtons
          form={form}
          isOpen={isOpen}
          checkedPermissions={checkedPermissions}
        />
      </Form>
    </Drawer>
  );
};

export default UserCompanyPermissionsForm;
